/**
 * DashboardReadService.js
 *
 * Service for reading dashboard data from Firestore.
 */

// Firebase
import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Constants
const DASHBOARDS_COLLECTION = collections.dashboards;

function normalizeScope(scope) {
  const candidate = typeof scope === 'string' ? scope.trim().toUpperCase() : '';
  if (candidate === 'ALL') return 'ALL';
  if (candidate === 'NODE_TYPE') return 'NODE_TYPE';
  return 'GLOBAL';
}

/**
 * Subscribes to dashboards for a workspace.
 *
 * @param {string} workspaceKey - Workspace key.
 * @param {function} callback - Update callback.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToDashboards(workspaceKey, callback, options = {}) {

  if (typeof callback !== 'function') {
    return () => {};
  }

  const requestedScope = normalizeScope(options?.scope);

  const q = workspaceKey
    ? query(collection(db, DASHBOARDS_COLLECTION), where('workspaceKey', '==', workspaceKey))
    : query(collection(db, DASHBOARDS_COLLECTION));

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() || {}),
        }))
        .filter((row) => {
          if (requestedScope === 'ALL') return true;
          const rowScope = normalizeScope(row?.scope);
          return rowScope === requestedScope;
        })
        .sort((a, b) => {
          const aTitle = (a?.titleLower || a?.title || '').toLowerCase();
          const bTitle = (b?.titleLower || b?.title || '').toLowerCase();
          return aTitle.localeCompare(bTitle);
        });
      callback(rows);
    },
    (error) => {
      console.error('[DashboardReadService] subscribeToDashboards error:', error);
      callback([]);
    }
  );
}

/**
 * Subscribes to a single dashboard by key.
 *
 * @param {string} key - Dashboard key.
 * @param {function} callback - Update callback.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToDashboard(key, callback) {

  if (typeof callback !== 'function') {
    return () => {};
  }

  if (!key) {
    callback(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, DASHBOARDS_COLLECTION, key),
    (docSnap) => {
      const data = docSnap.exists()
        ? { id: docSnap.id, ...(docSnap.data() || {}), key: docSnap.id }
        : null;
      callback(data);
    },
    (error) => {
      console.error('[DashboardReadService] subscribeToDashboard error:', error);
      callback(null);
    }
  );
}

/**
 * Fetches a single dashboard by key.
 *
 * @param {string} key - Dashboard key.
 *
 * @returns {Promise<Object|null>} Dashboard object or null.
 */
export async function getDashboard(key) {

  if (!key) return null;

  try {
    const ref = doc(db, DASHBOARDS_COLLECTION, key);
    const snap = await getDoc(ref);

    if (!snap.exists()) return null;

    const data = snap.data() || {};
    return { id: snap.id, ...data, key: snap.id };
  } catch (error) {
    console.error('[DashboardReadService] getDashboard error:', error);
    throw error;
  }
}

/**
 * Subscribes to a node-type-scoped Home dashboard by anchor type key.
 *
 * @param {Object} options - Subscription options.
 * @param {string} options.typeKey - Anchor node type key.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {function} callback - Update callback.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToNodeTypeDashboard({ typeKey, workspaceKey } = {}, callback) {
  const cleanedTypeKey = typeof typeKey === 'string' ? typeKey.trim() : '';
  const cleanedWorkspaceKey = typeof workspaceKey === 'string' ? workspaceKey.trim() : '';

  if (!cleanedTypeKey || !cleanedWorkspaceKey) {
    if (typeof callback === 'function') callback(null);
    return () => {};
  }

  if (typeof callback !== 'function') {
    return () => {};
  }

  const q = query(
    collection(db, DASHBOARDS_COLLECTION),
    where('workspaceKey', '==', cleanedWorkspaceKey),
    where('scope', '==', 'NODE_TYPE'),
    where('anchorTypeKey', '==', cleanedTypeKey)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));

      const row = rows[0] || null;
      callback(row);
    },
    (error) => {
      console.error('[DashboardReadService] subscribeToNodeTypeDashboard error:', error);
      callback(null);
    }
  );
}
